import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiBase, authHeaders } from './utils/authHeaders';

const API_URL = apiBase();

export const FIRST_RUN_STEP_META = [
  {
    id: 'upload_invoice',
    title: 'Upload a supplier invoice',
    blurb: 'PDF, photo, or zip via Cost Intelligence pipeline upload — extraction feeds the review queue.',
    href: '/cost-intel',
    cta: 'Upload invoices',
  },
  {
    id: 'review_lines',
    title: 'Review extracted lines',
    blurb: 'Confirm SKUs and prices in the review queue.',
    href: '/review',
    cta: 'Open review',
  },
  {
    id: 'cost_dish',
    title: 'Cost one dish',
    blurb: 'Confirm a draft recipe or open Recipes to cost a menu item.',
    href: '/chef-confirm',
    cta: 'Chef confirm',
  },
  {
    id: 'see_gp',
    title: 'See GP% and £ recoverable',
    blurb: 'Site rollup shows margin recovery from under-priced dishes.',
    href: '/roi',
    cta: 'Open ROI',
  },
];

/** Pure — pick next incomplete step from API status. */
export function nextIncompleteStep(status) {
  const steps = status?.steps || {};
  for (const meta of FIRST_RUN_STEP_META) {
    if (!(steps[meta.id] || {}).done) return meta.id;
  }
  return null;
}

/** Pure — progress fraction 0–1. */
export function firstRunProgress(status) {
  const total = status?.total || FIRST_RUN_STEP_META.length;
  const done = status?.done_count || 0;
  if (total <= 0) return 0;
  return Math.min(1, done / total);
}

export default function FirstRunWizard() {
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyStep, setBusyStep] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/api/trial/first-run`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setStatus(await res.json());
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const markDone = async (stepId) => {
    setBusyStep(stepId);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/api/trial/first-run/${stepId}/complete`, {
        method: 'POST',
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setStatus(await res.json());
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setBusyStep(null);
    }
  };

  const loadDemoSeed = async () => {
    setBusyStep('demo-seed');
    setError(null);
    try {
      const res = await fetch(`${API_URL}/api/trial/demo-seed`, {
        method: 'POST',
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      await res.json();
      await load();
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setBusyStep(null);
    }
  };

  const progress = firstRunProgress(status);
  const nextId = nextIncompleteStep(status);

  return (
    <div className="max-w-2xl mx-auto px-4 py-10" data-testid="first-run-wizard">
      <p className="text-sm font-medium text-emerald-700 mb-1">Getting started</p>
      <h1 className="text-2xl font-semibold text-slate-900 mb-2">Your first margin recovery</h1>
      <p className="text-slate-600 text-sm mb-4">
        Four steps from invoice to GP%. Mark each done when you finish it — or load demo data to explore offline.
      </p>
      <button
        type="button"
        onClick={loadDemoSeed}
        disabled={busyStep === 'demo-seed'}
        className="mb-6 text-sm px-3 py-1.5 rounded-md border border-emerald-600 text-emerald-800 hover:bg-emerald-50 disabled:opacity-60"
        data-testid="first-run-demo-seed"
      >
        {busyStep === 'demo-seed' ? 'Seeding…' : 'Load sample invoice + dish (demo)'}
      </button>

      <div className="h-2 bg-slate-200 rounded-full mb-8 overflow-hidden" aria-hidden>
        <div
          className="h-full bg-emerald-600 transition-all"
          style={{ width: `${Math.round(progress * 100)}%` }}
          data-testid="first-run-progress"
        />
      </div>

      {loading && <p className="text-slate-500">Loading…</p>}
      {error && (
        <p className="text-rose-700 mb-4" role="alert">
          {error}
        </p>
      )}

      {!loading && status?.is_complete && (
        <div className="mb-6 p-4 bg-emerald-50 border border-emerald-200 rounded-lg" data-testid="first-run-complete">
          <p className="font-medium text-emerald-900">First-run complete</p>
          <p className="text-sm text-emerald-800 mt-1">
            Keep uploading invoices — Cost Intelligence and ROI update as prices land.
          </p>
          <Link to="/cost-intel" className="inline-block mt-3 text-emerald-800 underline text-sm">
            Go to Cost Intelligence
          </Link>
        </div>
      )}

      <ol className="space-y-4">
        {FIRST_RUN_STEP_META.map((meta, idx) => {
          const done = !!(status?.steps?.[meta.id] || {}).done;
          const isNext = meta.id === nextId;
          return (
            <li
              key={meta.id}
              className={`border rounded-lg p-4 ${done ? 'border-emerald-200 bg-emerald-50/40' : isNext ? 'border-slate-400 bg-white' : 'border-slate-200 bg-white'}`}
              data-testid={`first-run-step-${meta.id}`}
            >
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-xs text-slate-500 mb-1">Step {idx + 1}</p>
                  <h2 className="font-medium text-slate-900">{meta.title}</h2>
                  <p className="text-sm text-slate-600 mt-1">{meta.blurb}</p>
                </div>
                {done ? (
                  <span className="text-xs font-medium text-emerald-700 shrink-0">Done</span>
                ) : (
                  <span className="text-xs text-slate-400 shrink-0">{isNext ? 'Next' : ''}</span>
                )}
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                <Link
                  to={meta.href}
                  className="text-sm px-3 py-1.5 rounded-md bg-slate-900 text-white hover:bg-slate-800"
                >
                  {meta.cta}
                </Link>
                {!done && (
                  <button
                    type="button"
                    disabled={busyStep === meta.id}
                    onClick={() => markDone(meta.id)}
                    className="text-sm px-3 py-1.5 rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-60"
                    data-testid={`first-run-complete-${meta.id}`}
                  >
                    {busyStep === meta.id ? 'Saving…' : 'Mark done'}
                  </button>
                )}
              </div>
            </li>
          );
        })}
      </ol>

      <p className="mt-8 text-sm text-slate-500">
        <Link to="/dashboard" className="underline">Skip to dashboard</Link>
      </p>
    </div>
  );
}
